"use client"

import { useId, useMemo } from "react"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "../lib/utils"
import {
  FieldAriaContext,
  resolveFieldError,
  useFieldAriaRegistry,
  useRegisterFieldSlot,
  type FieldErrorIssue,
  type UseFieldErrorResult,
} from "./field-aria"
import { Label } from "./label"
import { Separator } from "./separator"

function FieldSet({ className, ...props }: React.ComponentProps<"fieldset">) {
  return (
    <fieldset
      data-slot="field-set"
      className={cn(
        "flex flex-col gap-6 has-[>[data-slot=checkbox-group]]:gap-3 has-[>[data-slot=radio-group]]:gap-3",
        className
      )}
      {...props}
    />
  )
}

function FieldLegend({
  className,
  variant = "legend",
  ...props
}: React.ComponentProps<"legend"> & { variant?: "legend" | "label" }) {
  return (
    <legend
      data-slot="field-legend"
      data-variant={variant}
      className={cn(
        "mb-3 font-medium data-[variant=label]:text-sm data-[variant=legend]:text-base",
        className
      )}
      {...props}
    />
  )
}

function FieldGroup({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="field-group"
      className={cn(
        "group/field-group @container/field-group flex w-full flex-col gap-7 data-[slot=checkbox-group]:gap-3 *:data-[slot=field-group]:gap-4",
        className
      )}
      {...props}
    />
  )
}

const fieldVariants = cva(
  "group/field flex w-full gap-3 data-[invalid=true]:text-destructive",
  {
    variants: {
      orientation: {
        vertical: "flex-col *:w-full [&>.sr-only]:w-auto",
        horizontal:
          "flex-row items-center has-[>[data-slot=field-content]]:items-start *:data-[slot=field-label]:flex-auto has-[>[data-slot=field-content]]:[&>[role=checkbox],[role=radio]]:mt-px",
        responsive:
          "flex-col *:w-full @md/field-group:flex-row @md/field-group:items-center @md/field-group:*:w-auto @md/field-group:has-[>[data-slot=field-content]]:items-start @md/field-group:*:data-[slot=field-label]:flex-auto [&>.sr-only]:w-auto @md/field-group:has-[>[data-slot=field-content]]:[&>[role=checkbox],[role=radio]]:mt-px",
      },
    },
    defaultVariants: {
      orientation: "vertical",
    },
  }
)

function Field({
  className,
  orientation = "vertical",
  ...props
}: React.ComponentProps<"div"> & VariantProps<typeof fieldVariants>) {
  // WCAG 3.3.1 — the Field wrapper owns the aria registry: FieldDescription /
  // FieldError register their ids while rendered, and the ui-kit controls
  // (Input, Textarea, SelectTrigger, …) read it to derive `aria-invalid` +
  // `aria-describedby` with zero per-call wiring. A consumer-declared
  // `data-invalid` also marks the control invalid.
  const dataInvalid = (props as Record<string, unknown>)["data-invalid"]
  const aria = useFieldAriaRegistry(dataInvalid === true || dataInvalid === "true")
  return (
    <FieldAriaContext.Provider value={aria}>
      <div
        role="group"
        data-slot="field"
        data-orientation={orientation}
        className={cn(fieldVariants({ orientation }), className)}
        {...props}
      />
    </FieldAriaContext.Provider>
  )
}

function FieldContent({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="field-content"
      className={cn(
        "group/field-content flex flex-1 flex-col gap-1 leading-snug",
        className
      )}
      {...props}
    />
  )
}

function FieldLabel({
  className,
  ...props
}: React.ComponentProps<typeof Label>) {
  return (
    <Label
      data-slot="field-label"
      className={cn(
        "group/field-label peer/field-label flex w-fit gap-2 leading-snug group-data-[disabled=true]/field:opacity-50 has-data-checked:border-primary/30 has-data-checked:bg-primary/5 has-[>[data-slot=field]]:rounded-xl has-[>[data-slot=field]]:border *:data-[slot=field]:p-4 dark:has-data-checked:border-primary/20 dark:has-data-checked:bg-primary/10",
        "has-[>[data-slot=field]]:w-full has-[>[data-slot=field]]:flex-col",
        className
      )}
      {...props}
    />
  )
}

function FieldTitle({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="field-label"
      className={cn(
        "flex w-fit items-center gap-2 text-sm font-medium group-data-[disabled=true]/field:opacity-50",
        className
      )}
      {...props}
    />
  )
}

function FieldDescription({ className, id, ...props }: React.ComponentProps<"p">) {
  // Inside a Field, the description self-registers so the control's
  // `aria-describedby` references it (explicit `id` wins; outside a Field
  // the markup is unchanged).
  const resolvedId = useRegisterFieldSlot("description", id, true)
  return (
    <p
      id={resolvedId}
      data-slot="field-description"
      className={cn(
        "text-left text-sm leading-normal font-normal text-muted-foreground group-has-data-horizontal/field:text-balance [[data-variant=legend]+&]:-mt-1.5",
        "last:mt-0 nth-last-2:-mt-1",
        "[&>a]:underline [&>a]:underline-offset-4 [&>a:hover]:text-primary",
        className
      )}
      {...props}
    />
  )
}

function FieldSeparator({
  children,
  className,
  ...props
}: React.ComponentProps<"div"> & {
  children?: React.ReactNode
}) {
  return (
    <div
      data-slot="field-separator"
      data-content={!!children}
      className={cn(
        "relative -my-2 h-5 text-sm group-data-[variant=outline]/field-group:-mb-2",
        className
      )}
      {...props}
    >
      <Separator className="absolute inset-0 top-1/2" />
      {children && (
        <span
          className="relative mx-auto block w-fit bg-background px-2 text-muted-foreground"
          data-slot="field-separator-content"
        >
          {children}
        </span>
      )}
    </div>
  )
}

function FieldError({
  className,
  children,
  errors,
  id,
  ...props
}: React.ComponentProps<"div"> & {
  errors?: Array<{ message?: string } | undefined>
}) {
  const content = useMemo(() => {
    if (children) {
      return children
    }

    if (!errors?.length) {
      return null
    }

    const uniqueErrors = [
      ...new Map(errors.map((error) => [error?.message, error])).values(),
    ]

    if (uniqueErrors?.length == 1) {
      return uniqueErrors[0]?.message
    }

    return (
      <ul className="ml-4 flex list-disc flex-col gap-1">
        {uniqueErrors.map(
          (error, index) =>
            error?.message && <li key={index}>{error.message}</li>
        )}
      </ul>
    )
  }, [children, errors])

  // Inside a Field, register the error id ONLY while content renders — the
  // enclosing Field then flips the control to `aria-invalid="true"` and
  // appends this id to its `aria-describedby`; when the error clears, the
  // registration is released and the control drops both attributes (no
  // dangling references). The rendered element keeps `role="alert"` so the
  // message is announced assertively when it appears.
  const resolvedId = useRegisterFieldSlot("error", id, content != null)

  if (!content) {
    return null
  }

  return (
    <div
      role="alert"
      id={resolvedId}
      data-slot="field-error"
      className={cn("text-sm font-normal text-destructive", className)}
      {...props}
    >
      {content}
    </div>
  )
}

/**
 * Per-field error association for the hand-built validation pattern (WCAG
 * 3.3.1). Given the flat issue list a view already tracks and the field name,
 * returns a stable `errorId` plus ready-to-spread `aria-invalid` /
 * `aria-describedby` props, so a screen-reader user hears *why* a field is
 * invalid — not just its label.
 *
 *   const nameErr = useFieldError(errors, 'name');
 *   <Input aria-required="true" {...nameErr.fieldProps} />
 *   <FieldError id={nameErr.errorId} errors={nameErr.errors} />
 *
 * Pass an explicit `idPrefix` when the same field renders in more than one
 * place (e.g. repeated line rows) so the generated ids stay unique.
 */
function useFieldError(
  issues: ReadonlyArray<FieldErrorIssue> | undefined,
  field: string,
  idPrefix?: string
): UseFieldErrorResult {
  const reactId = useId()
  return useMemo(
    () => resolveFieldError(issues, field, idPrefix ?? reactId),
    [issues, field, idPrefix, reactId]
  )
}

// Re-export the aria plumbing (pure helpers + control hook) so consumers and
// the other primitives import it from the same barrel surface as Field.
export {
  addAriaId,
  composeDescribedBy,
  FieldAriaContext,
  removeAriaId,
  resolveFieldControlAria,
  resolveFieldError,
  useFieldAriaRegistry,
  useFieldControlAria,
  useRegisterFieldSlot,
  type ExplicitControlAria,
  type FieldAriaContextValue,
  type FieldAriaSnapshot,
  type FieldErrorIssue,
  type ResolvedControlAria,
  type UseFieldErrorResult,
} from "./field-aria"

export {
  Field,
  FieldLabel,
  FieldDescription,
  FieldError,
  useFieldError,
  FieldGroup,
  FieldLegend,
  FieldSeparator,
  FieldSet,
  FieldContent,
  FieldTitle,
}
